// Production Scheduling — 라인별 Gantt SVG (명세서 FR1 "간트차트")
import { useEffect, useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Card } from '../components/Card'
import { Button } from '../components/Button'
import { Skeleton } from '../components/Skeleton'
import { api } from '../lib/api'
import { useToast } from '../components/Toast'
import { useGlobalReveal } from '../hooks/useReveal'
import { useAuth, hasPerm } from '../lib/auth'
import { useT } from '../lib/i18n'

interface GanttResp {
  window: { start: string; end: string; days: number; today: string }
  lines: string[]
  work_orders: Array<{
    work_order_id: string
    production_order_id: string
    line_id: string
    equipment_id: string
    worker_id: string
    start_date: string
    end_date: string
    status: 'created' | 'running' | 'done' | 'cancelled'
    priority: number
    scheduled_qty: number | null
    item_code: string
    po_quantity: number
    due_date: string
  }>
}

const STATUS_FILL: Record<GanttResp['work_orders'][number]['status'], string> = {
  created:   'color-mix(in oklab, var(--color-ink-3) 30%, transparent)',
  running:   'color-mix(in oklab, var(--color-accent) 75%, transparent)',
  done:      'color-mix(in oklab, var(--color-success) 65%, transparent)',
  cancelled: 'color-mix(in oklab, var(--color-danger) 40%, transparent)',
}
const STATUS_BORDER: Record<GanttResp['work_orders'][number]['status'], string> = {
  created:   'var(--color-line-strong)',
  running:   'var(--color-accent)',
  done:      'var(--color-success)',
  cancelled: 'var(--color-danger)',
}

function dayIndex(d: string, start: string): number {
  const ms = (new Date(d).getTime() - new Date(start).getTime()) / (1000 * 60 * 60 * 24)
  return Math.round(ms)
}

export function SchedulePage() {
  useGlobalReveal()
  const toast = useToast()
  const { t } = useT()
  const { user } = useAuth()
  const canWrite = hasPerm(user, 'schedule', 'write')

  const [days, setDays] = useState(14)
  const [data, setData] = useState<GanttResp | null>(null)
  const [selected, setSelected] = useState<GanttResp['work_orders'][number] | null>(null)
  const [editPrio, setEditPrio] = useState<string>('5')
  const [busy, setBusy] = useState(false)

  const load = async () => {
    try {
      const d = await api.get<GanttResp>(`/schedule/gantt?days=${days}`)
      setData(d)
    } catch (e: any) {
      toast.push('error', e?.message ?? '조회 실패')
    }
  }
  useEffect(() => { load() }, [days])

  const adjust = async (newPriority: number) => {
    if (!selected) return
    setBusy(true)
    try {
      await api.put(`/schedule/work-orders/${selected.work_order_id}`, { priority: newPriority })
      toast.push('success', `${selected.work_order_id} priority → ${newPriority}`)
      setSelected(null)
      load()
    } catch (e: any) {
      toast.push('error', e?.message ?? '실패')
    } finally { setBusy(false) }
  }

  const layout = useMemo(() => {
    if (!data) return null
    const totalDays = days
    const ROW_H = 56
    const HEADER_H = 36
    const LABEL_W = 90
    const DAY_W = 64
    const W = LABEL_W + totalDays * DAY_W
    const H = HEADER_H + data.lines.length * ROW_H + 24
    return { totalDays, ROW_H, HEADER_H, LABEL_W, DAY_W, W, H }
  }, [data, days])

  return (
    <main id="main" className="container stack" style={{ paddingBlock: '2rem' }}>
      <div className="row space-between reveal">
        <div>
          <h1 className="page-title" style={{ marginBottom: '0.2rem' }}>
            <FontAwesomeIcon icon="calendar-days" /> {t('sched.title')}
          </h1>
          <p className="muted" style={{ marginBottom: 0 }}>
            {t('sched.intro')}
          </p>
        </div>
        <div className="row" style={{ gap: '0.4rem' }}>
          {[7, 14, 30].map((n) => (
            <Button
              key={n}
              variant={days === n ? 'accent' : 'ghost'}
              magnetic={false}
              onClick={() => setDays(n)}
            >
              {n}일
            </Button>
          ))}
        </div>
      </div>

      <Card glass tilt={false} spotlight={false} className="reveal" as="section">
        {!data || !layout ? (
          <Skeleton h={300} />
        ) : data.work_orders.length === 0 ? (
          <div className="empty-state" role="status">
            <FontAwesomeIcon icon="calendar-days" className="icon-lg" />
            <p className="muted" style={{ margin: 0 }}>해당 기간에 스케줄된 작업이 없습니다.</p>
          </div>
        ) : (
          <>
          <div
            className="row"
            aria-label="범례"
            style={{ gap: '0.85rem', flexWrap: 'wrap', marginBottom: '0.75rem', fontSize: 'var(--fs-xs)' }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
              <span style={{ width: 14, height: 10, borderRadius: 3, background: STATUS_FILL.created, border: `1px solid ${STATUS_BORDER.created}` }} />
              계획됨
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
              <span style={{ width: 14, height: 10, borderRadius: 3, background: STATUS_FILL.running, border: `1px solid ${STATUS_BORDER.running}` }} />
              진행 중
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
              <span style={{ width: 14, height: 10, borderRadius: 3, background: STATUS_FILL.done, border: `1px solid ${STATUS_BORDER.done}` }} />
              완료
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
              <span style={{ width: 14, height: 10, borderRadius: 3, background: STATUS_FILL.cancelled, border: `1px solid ${STATUS_BORDER.cancelled}` }} />
              취소
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
              <span style={{ width: 3, height: 12, background: 'var(--color-danger)', borderRadius: 2 }} />
              긴급 (P1)
            </span>
          </div>
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <svg
              viewBox={`0 0 ${layout.W} ${layout.H}`}
              role="img"
              aria-label="Gantt 차트"
              style={{ width: layout.W, maxWidth: 'none', height: 'auto', display: 'block' }}
            >
              {/* 날짜 헤더 */}
              {Array.from({ length: layout.totalDays }).map((_, i) => {
                const d = new Date(data.window.start)
                d.setDate(d.getDate() + i)
                const iso = d.toISOString().slice(0, 10)
                const dow = d.toLocaleDateString('ko-KR', { weekday: 'short' })
                const isToday = iso === data.window.today
                const isWeekend = d.getDay() === 0 || d.getDay() === 6
                return (
                  <g key={i}>
                    {isWeekend && (
                      <rect
                        x={layout.LABEL_W + i * layout.DAY_W}
                        y={layout.HEADER_H}
                        width={layout.DAY_W}
                        height={layout.H - layout.HEADER_H}
                        fill="var(--color-bg-soft)"
                        opacity={0.6}
                      />
                    )}
                    {isToday && (
                      <rect
                        x={layout.LABEL_W + i * layout.DAY_W}
                        y={0}
                        width={layout.DAY_W}
                        height={layout.H}
                        fill="var(--color-accent-soft)"
                      />
                    )}
                    <text
                      x={layout.LABEL_W + i * layout.DAY_W + layout.DAY_W / 2}
                      y={14}
                      textAnchor="middle"
                      fontSize="11"
                      fill={isToday ? 'var(--color-accent)' : 'var(--color-ink-3)'}
                      fontWeight={isToday ? 700 : 400}
                    >{d.getDate()}</text>
                    <text
                      x={layout.LABEL_W + i * layout.DAY_W + layout.DAY_W / 2}
                      y={28}
                      textAnchor="middle"
                      fontSize="10"
                      fill="var(--color-ink-3)"
                    >{dow}</text>
                    <line
                      x1={layout.LABEL_W + i * layout.DAY_W}
                      y1={layout.HEADER_H}
                      x2={layout.LABEL_W + i * layout.DAY_W}
                      y2={layout.H}
                      stroke="var(--color-line)"
                    />
                  </g>
                )
              })}

              {/* 라인 라벨 + 행 구분 */}
              {data.lines.map((line, lineIdx) => {
                const y = layout.HEADER_H + lineIdx * layout.ROW_H
                return (
                  <g key={line}>
                    <text
                      x={layout.LABEL_W - 12}
                      y={y + layout.ROW_H / 2 + 4}
                      textAnchor="end"
                      fontSize="13"
                      fontWeight={600}
                      fill="var(--color-ink)"
                    >{line}</text>
                    <line
                      x1={0}
                      y1={y + layout.ROW_H}
                      x2={layout.W}
                      y2={y + layout.ROW_H}
                      stroke="var(--color-line)"
                    />
                  </g>
                )
              })}

              {/* 작업지시 막대 */}
              {data.work_orders.map((wo) => {
                const lineIdx = data.lines.indexOf(wo.line_id)
                if (lineIdx < 0) return null
                const startIdx = Math.max(0, dayIndex(wo.start_date, data.window.start))
                const endIdx = Math.min(layout.totalDays, dayIndex(wo.end_date, data.window.start) + 1)
                const spanDays = Math.max(1, endIdx - startIdx)
                const x = layout.LABEL_W + startIdx * layout.DAY_W + 4
                const y = layout.HEADER_H + lineIdx * layout.ROW_H + 8
                const w = spanDays * layout.DAY_W - 8
                const h = layout.ROW_H - 16
                return (
                  <g
                    key={wo.work_order_id}
                    onClick={() => { setSelected(wo); setEditPrio(String(wo.priority)) }}
                    style={{ cursor: 'pointer' }}
                  >
                    <rect
                      x={x}
                      y={y}
                      width={w}
                      height={h}
                      rx={8}
                      ry={8}
                      fill={STATUS_FILL[wo.status]}
                      stroke={STATUS_BORDER[wo.status]}
                      strokeWidth={wo.priority <= 2 ? 2 : 1}
                    >
                      <title>
                        {wo.work_order_id} · {wo.item_code} · {wo.start_date} → {wo.end_date}
                        {'\n'}수량 {wo.scheduled_qty ?? wo.po_quantity} · priority {wo.priority} · {wo.status}
                      </title>
                    </rect>
                    {wo.priority <= 2 && (
                      <rect
                        x={x}
                        y={y}
                        width={4}
                        height={h}
                        fill="var(--color-danger)"
                        rx={2}
                      />
                    )}
                    <text
                      x={x + 8}
                      y={y + 16}
                      fontSize="11"
                      fontWeight={600}
                      fill="var(--color-ink)"
                    >
                      {wo.item_code}
                    </text>
                    <text
                      x={x + 8}
                      y={y + 30}
                      fontSize="10"
                      fill="var(--color-ink-2)"
                    >
                      {wo.work_order_id} · P{wo.priority}
                    </text>
                  </g>
                )
              })}
            </svg>
          </div>
          <p className="muted" style={{ marginTop: '0.6rem', fontSize: 'var(--fs-xs)' }}>
            막대 클릭 → 우선순위 조정
          </p>
          </>
        )}
      </Card>

      {/* 우선순위 조정 모달 */}
      {selected && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="우선순위 조정"
          onClick={(e) => { if (e.target === e.currentTarget) setSelected(null) }}
          style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', display: 'grid', placeItems: 'center', padding: '1rem' }}
        >
          <Card glass tilt={false} spotlight={false} style={{ width: 'min(440px, 100%)' }}>
            <h2 style={{ fontSize: 'var(--fs-lg)' }}>
              <span className="mono">{selected.work_order_id}</span>
            </h2>
            <p className="muted" style={{ marginBottom: '1rem', fontSize: 'var(--fs-sm)' }}>
              {selected.item_code} · {selected.line_id} · {selected.start_date} → {selected.end_date}
            </p>
            <div className="field">
              <label htmlFor="prio">우선순위 (1 = 최긴급, 9 = 최저)</label>
              <input
                id="prio"
                type="number"
                min="1"
                max="9"
                value={editPrio}
                onChange={(e) => setEditPrio(e.target.value)}
              />
            </div>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <Button variant="ghost" magnetic={false} onClick={() => setSelected(null)}>취소</Button>
              <div className="row" style={{ gap: '0.4rem' }}>
                <Button
                  variant="danger"
                  magnetic={false}
                  disabled={!canWrite || busy}
                  onClick={() => adjust(1)}
                  aria-label="긴급(1)으로 변경"
                >
                  <FontAwesomeIcon icon="bolt" />
                  <span>긴급(1)</span>
                </Button>
                <Button
                  variant="accent"
                  magnetic={false}
                  disabled={!canWrite || busy}
                  onClick={() => {
                    const p = Math.max(1, Math.min(9, Number(editPrio) | 0))
                    adjust(p)
                  }}
                >
                  <FontAwesomeIcon icon={busy ? 'circle-notch' : 'check'} spin={busy} />
                  <span>적용</span>
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </main>
  )
}
